import { printDocument } from './print-utils';
import { formatActor, UNKNOWN_ACTOR_SHORT } from './audit-format';

/**
 * Jinda POS - Reusable Record Print Utilities
 * Generates beautifully formatted A4 vouchers and summary cards for POS modules.
 */

const getBaseHeader = (docTitle: string, docNo?: string, dateStr?: string) => `
    <div style="display: flex; justify-content: space-between; border-bottom: 2px solid #FF7A59; padding-bottom: 14px; margin-bottom: 18px;">
        <div>
            <h1 style="font-size: 22px; font-weight: 700; color: #FF7A59; margin: 0;">🥕 JinDa POS Store</h1>
            <p style="font-size: 11px; color: #64748B; margin: 3px 0 0 0;">
                123/45 ถนนสุขุมวิท กรุงเทพมหานคร 10110 | โทร: 02-123-4567 | เลขผู้เสียภาษี: 0105559999999
            </p>
        </div>
        <div style="text-align: right;">
            <h2 style="font-size: 18px; font-weight: 700; color: #1E293B; margin: 0;">${docTitle}</h2>
            ${docNo ? `<p style="font-size: 13px; font-weight: 600; color: #FF7A59; margin: 3px 0 0 0;">เลขที่: ${docNo}</p>` : ''}
            <p style="font-size: 11px; color: #64748B; margin: 0;">วันที่พิมพ์: ${dateStr || new Date().toLocaleString('th-TH')}</p>
        </div>
    </div>
`;

const getSignatures = (sign1 = 'ผู้จัดทำ / ผู้บันทึก', sign2 = 'ผู้รับมอบ / ผู้ปฏิบัติงาน', sign3 = 'ผู้จัดการ / ผู้อนุมัติ') => `
    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; text-align: center; font-size: 11px; color: #64748B; margin-top: 36px; page-break-inside: avoid;">
        <div>
            <div style="border-bottom: 1px dotted #94A3B8; height: 45px; margin-bottom: 6px;"></div>
            <p style="margin: 0; font-weight: 600; color: #334155;">${sign1}</p>
            <p style="margin: 2px 0 0 0; font-size: 10px;">วันที่: ......./......./...........</p>
        </div>
        <div>
            <div style="border-bottom: 1px dotted #94A3B8; height: 45px; margin-bottom: 6px;"></div>
            <p style="margin: 0; font-weight: 600; color: #334155;">${sign2}</p>
            <p style="margin: 2px 0 0 0; font-size: 10px;">วันที่: ......./......./...........</p>
        </div>
        <div>
            <div style="border-bottom: 1px dotted #94A3B8; height: 45px; margin-bottom: 6px;"></div>
            <p style="margin: 0; font-weight: 600; color: #334155;">${sign3}</p>
            <p style="margin: 2px 0 0 0; font-size: 10px;">วันที่: ......./......./...........</p>
        </div>
    </div>
`;

export const printCheckStockDocument = (data: any) => {
    const container = document.createElement('div');
    container.style.padding = '24px';
    container.style.fontFamily = 'Kodchasan, Prompt, sans-serif';
    container.style.color = '#1E293B';
    container.style.background = '#FFFFFF';

    const items = data.detail || [];
    container.innerHTML = `
        ${getBaseHeader('ใบนับสต๊อกสินค้า (STOCK AUDIT)', data.document_no, data.create_date)}
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; background: #F8FAFC; padding: 12px; border-radius: 8px; margin-bottom: 18px; border: 1px solid #E2E8F0; font-size: 12px;">
            <div>
                <strong>สถานะการอัพเดทสต๊อก:</strong> ${data.is_update_stock == 1 ? '<span style="color: #10B981; font-weight: 700;">🟢 อัพเดทสต๊อกแล้ว</span>' : '<span style="color: #F59E0B; font-weight: 700;">🟡 ไม่อัพเดทสต๊อก</span>'}
            </div>
            <div>
                <strong>ผู้ทำรายการ:</strong> ${formatActor(data.create_by_name, UNKNOWN_ACTOR_SHORT)}
            </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 18px;">
            <thead>
                <tr style="background: #FFF3EE; border-bottom: 2px solid #FFCBBB; color: #FF7A59;">
                    <th style="padding: 6px; text-align: center; width: 40px; border: 1px solid #FFD8CC;">#</th>
                    <th style="padding: 6px 10px; text-align: left; border: 1px solid #FFD8CC;">รหัสสินค้า</th>
                    <th style="padding: 6px 10px; text-align: left; border: 1px solid #FFD8CC;">ชื่อสินค้า</th>
                    <th style="padding: 6px 10px; text-align: center; border: 1px solid #FFD8CC;">จำนวนในระบบ</th>
                    <th style="padding: 6px 10px; text-align: center; border: 1px solid #FFD8CC;">นับจริง</th>
                    <th style="padding: 6px 10px; text-align: center; border: 1px solid #FFD8CC;">ผลต่าง (ขาด/เกิน)</th>
                </tr>
            </thead>
            <tbody>
                ${items.length > 0 ? items.map((item: any, idx: number) => `
                    <tr style="border-bottom: 1px solid #E2E8F0;">
                        <td style="padding: 6px; text-align: center; border: 1px solid #E2E8F0;">${idx + 1}</td>
                        <td style="padding: 6px 10px; font-weight: 600; border: 1px solid #E2E8F0;">${item.product_code || '-'}</td>
                        <td style="padding: 6px 10px; border: 1px solid #E2E8F0;">${item.product_name || '-'}</td>
                        <td style="padding: 6px 10px; text-align: center; border: 1px solid #E2E8F0;">${item.current_quantity ?? '-'}</td>
                        <td style="padding: 6px 10px; text-align: center; font-weight: 700; border: 1px solid #E2E8F0;">${item.actual_quantity ?? '-'}</td>
                        <td style="padding: 6px 10px; text-align: center; border: 1px solid #E2E8F0; color: ${(item.diff || 0) < 0 ? '#EF4444' : (item.diff || 0) > 0 ? '#10B981' : '#64748B'};">
                            ${item.diff > 0 ? `+${item.diff}` : item.diff || 0}
                        </td>
                    </tr>
                `).join('') : `
                    <tr>
                        <td colspan="6" style="text-align: center; padding: 16px; color: #94A3B8; border: 1px solid #E2E8F0;">
                            จำนวนสินค้าปกติ: ${data.quantity || 0} | หาย: ${data.lost || 0} | เกิน: ${data.over || 0}
                        </td>
                    </tr>
                `}
            </tbody>
        </table>

        ${getSignatures('ผู้นับสต๊อก', 'ผู้ตรวจสอบความถูกต้อง', 'ผู้จัดการร้าน')}
    `;

    printDocument(container, {
        documentType: 'a4-portrait',
        title: `ใบนับสต๊อก ${data.document_no || ''}`
    });
};

export const printWithdrawDocument = (data: any) => {
    const container = document.createElement('div');
    container.style.padding = '24px';
    container.style.fontFamily = 'Kodchasan, Prompt, sans-serif';
    container.style.color = '#1E293B';
    container.style.background = '#FFFFFF';

    const items = data.detail || [];
    container.innerHTML = `
        ${getBaseHeader('ใบเบิกสินค้า / วัตถุดิบ (GOODS WITHDRAWAL)', data.document_no, data.create_date)}
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; background: #F8FAFC; padding: 12px; border-radius: 8px; margin-bottom: 18px; border: 1px solid #E2E8F0; font-size: 12px;">
            <div>
                <strong>ผู้เบิกสินค้า:</strong> ${formatActor(data.create_by_name, UNKNOWN_ACTOR_SHORT)}
            </div>
            <div>
                <strong>หมายเหตุ / วัตถุประสงค์:</strong> ${data.remark || 'เบิกใช้งานภายในร้าน'}
            </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 18px;">
            <thead>
                <tr style="background: #FFF3EE; border-bottom: 2px solid #FFCBBB; color: #FF7A59;">
                    <th style="padding: 6px; text-align: center; width: 40px; border: 1px solid #FFD8CC;">#</th>
                    <th style="padding: 6px 10px; text-align: left; border: 1px solid #FFD8CC;">รหัสสินค้า</th>
                    <th style="padding: 6px 10px; text-align: left; border: 1px solid #FFD8CC;">ชื่อสินค้า</th>
                    <th style="padding: 6px 10px; text-align: center; border: 1px solid #FFD8CC;">จำนวนที่เบิก</th>
                    <th style="padding: 6px 10px; text-align: right; border: 1px solid #FFD8CC;">ต้นทุน/หน่วย</th>
                    <th style="padding: 6px 10px; text-align: right; border: 1px solid #FFD8CC;">รวม (บาท)</th>
                </tr>
            </thead>
            <tbody>
                ${items.length > 0 ? items.map((item: any, idx: number) => `
                    <tr style="border-bottom: 1px solid #E2E8F0;">
                        <td style="padding: 6px; text-align: center; border: 1px solid #E2E8F0;">${idx + 1}</td>
                        <td style="padding: 6px 10px; font-weight: 600; border: 1px solid #E2E8F0;">${item.product_code || '-'}</td>
                        <td style="padding: 6px 10px; border: 1px solid #E2E8F0;">${item.product_name || '-'}</td>
                        <td style="padding: 6px 10px; text-align: center; font-weight: 700; border: 1px solid #E2E8F0;">${item.quantity || 0}</td>
                        <td style="padding: 6px 10px; text-align: right; border: 1px solid #E2E8F0;">${Number(item.cost || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
                        <td style="padding: 6px 10px; text-align: right; border: 1px solid #E2E8F0; font-weight: 600;">
                            ${Number((item.quantity * item.cost) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                        </td>
                    </tr>
                `).join('') : `
                    <tr>
                        <td colspan="6" style="text-align: center; padding: 16px; color: #94A3B8; border: 1px solid #E2E8F0;">
                            จำนวนรวม: ${data.quantity || 0} ชิ้น | ต้นทุนรวม: ${Number(data.cost || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท
                        </td>
                    </tr>
                `}
            </tbody>
        </table>

        ${getSignatures('ผู้ขอเบิก', 'ผู้จ่ายสินค้า/คลัง', 'ผู้อนุมัติการเบิก')}
    `;

    printDocument(container, {
        documentType: 'a4-portrait',
        title: `ใบเบิกสินค้า ${data.document_no || ''}`
    });
};

export const printSeparateDocument = (data: any) => {
    const container = document.createElement('div');
    container.style.padding = '24px';
    container.style.fontFamily = 'Kodchasan, Prompt, sans-serif';
    container.style.color = '#1E293B';
    container.style.background = '#FFFFFF';

    container.innerHTML = `
        ${getBaseHeader('ใบรายการแยกแพคสินค้า (PRODUCT SEPARATION)', data.document_no || '', data.create_date)}
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px;">
            <div style="border: 1px solid #FFD8CC; background: #FFF8F5; border-radius: 8px; padding: 14px;">
                <h3 style="color: #FF7A59; margin: 0 0 10px 0; font-size: 14px;">📦 สินค้าต้นทาง (ตัดสต๊อกออก)</h3>
                <div style="font-size: 13px; line-height: 1.8;">
                    <div><strong>ชื่อสินค้า:</strong> ${data.from_product_name || '-'}</div>
                    <div><strong>จำนวนที่ตัด:</strong> <span style="font-weight: 700; color: #EF4444;">${data.from_quantity || 0}</span> ${data.from_product_unit_name || ''}</div>
                </div>
            </div>

            <div style="border: 1px solid #C7D2FE; background: #EEF2FF; border-radius: 8px; padding: 14px;">
                <h3 style="color: #4F46E5; margin: 0 0 10px 0; font-size: 14px;">✨ สินค้าปลายทาง (เพิ่มสต๊อกเข้า)</h3>
                <div style="font-size: 13px; line-height: 1.8;">
                    <div><strong>ชื่อสินค้า:</strong> ${data.to_product_name || '-'}</div>
                    <div><strong>จำนวนที่ได้:</strong> <span style="font-weight: 700; color: #10B981;">${data.to_quantity || 0}</span> ${data.to_product_unit_name || ''}</div>
                </div>
            </div>
        </div>

        ${getSignatures('ผู้ทำการแยกแพค', 'ผู้ตรวจรับสต๊อก', 'หัวหน้าคลังสินค้า')}
    `;

    printDocument(container, {
        documentType: 'a4-portrait',
        title: `ใบแยกแพคสินค้า ${data.from_product_name || ''}`
    });
};

export const printIncludeDocument = (data: any) => {
    const container = document.createElement('div');
    container.style.padding = '24px';
    container.style.fontFamily = 'Kodchasan, Prompt, sans-serif';
    container.style.color = '#1E293B';
    container.style.background = '#FFFFFF';

    container.innerHTML = `
        ${getBaseHeader('ใบรายการรวมแพคสินค้า (PRODUCT BUNDLING)', data.document_no || '', data.create_date)}
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px;">
            <div style="border: 1px solid #FFD8CC; background: #FFF8F5; border-radius: 8px; padding: 14px;">
                <h3 style="color: #FF7A59; margin: 0 0 10px 0; font-size: 14px;">📦 สินค้าต้นทาง (ตัดสต๊อกออก)</h3>
                <div style="font-size: 13px; line-height: 1.8;">
                    <div><strong>ชื่อสินค้า:</strong> ${data.from_product_name || '-'}</div>
                    <div><strong>จำนวนที่ตัด:</strong> <span style="font-weight: 700; color: #EF4444;">${data.from_quantity || 0}</span> ${data.from_product_unit_name || ''}</div>
                </div>
            </div>

            <div style="border: 1px solid #C7D2FE; background: #EEF2FF; border-radius: 8px; padding: 14px;">
                <h3 style="color: #4F46E5; margin: 0 0 10px 0; font-size: 14px;">✨ สินค้าปลายทาง (เพิ่มสต๊อกเข้า)</h3>
                <div style="font-size: 13px; line-height: 1.8;">
                    <div><strong>ชื่อสินค้า:</strong> ${data.to_product_name || '-'}</div>
                    <div><strong>จำนวนที่ได้:</strong> <span style="font-weight: 700; color: #10B981;">${data.to_quantity || 0}</span> ${data.to_product_unit_name || ''}</div>
                </div>
            </div>
        </div>

        ${getSignatures('ผู้ทำการรวมแพค', 'ผู้ตรวจรับสต๊อก', 'หัวหน้าคลังสินค้า')}
    `;

    printDocument(container, {
        documentType: 'a4-portrait',
        title: `ใบรวมแพคสินค้า ${data.to_product_name || ''}`
    });
};

export const printProfileCard = (title: string, fields: { label: string; value: any }[]) => {
    const container = document.createElement('div');
    container.style.padding = '24px';
    container.style.fontFamily = 'Kodchasan, Prompt, sans-serif';
    container.style.color = '#1E293B';
    container.style.background = '#FFFFFF';

    container.innerHTML = `
        ${getBaseHeader(title)}
        <div style="max-width: 500px; margin: 20px auto; border: 1.5px solid #E2E8F0; border-radius: 12px; padding: 20px; background: #F8FAFC;">
            <div style="font-size: 14px; line-height: 2;">
                ${fields.map(f => `
                    <div style="display: flex; justify-content: space-between; border-bottom: 1px dashed #E2E8F0; padding: 4px 0;">
                        <span style="color: #64748B;">${f.label}:</span>
                        <strong style="color: #1E293B;">${f.value ?? '-'}</strong>
                    </div>
                `).join('')}
            </div>
        </div>
        ${getSignatures('ผู้จัดทำ', 'ผู้รับเอกสาร', 'ผู้จัดการ')}
    `;

    printDocument(container, {
        documentType: 'a4-portrait',
        title: title
    });
};

export const printVisualAnalyticsReport = (params: {
    viewMode: 'weekly' | 'monthly' | 'yearly';
    totalSales: number;
    growthPercent: number;
    totalBills: number;
    avgPerBill: number;
    peakText?: string;
    data: { label: string; value: number; compareValue?: number; bills?: number }[];
    paymentDonutData: { label: string; value: number; color: string; percentage: number }[];
    categoryDonutData: { label: string; value: number; color: string; percentage: number }[];
    hourlyData: { hour: string; sales: number; bills: number }[];
}) => {
    const {
        viewMode,
        totalSales,
        growthPercent,
        totalBills,
        avgPerBill,
        peakText = viewMode === 'weekly' ? 'วันเสาร์ (฿8,900)' : viewMode === 'monthly' ? 'ส.ค. (฿168,000)' : 'ปี 2025 (฿1,680,000)',
        data,
        paymentDonutData,
        categoryDonutData,
        hourlyData
    } = params;

    const container = document.createElement('div');
    container.className = 'analytics-print-doc';
    container.style.padding = '18px 24px';
    container.style.fontFamily = 'Kodchasan, Prompt, sans-serif';
    container.style.color = '#1E293B';
    container.style.background = '#FFFFFF';

    const maxBarVal = Math.max(...data.map(d => Math.max(d.value, d.compareValue || 0)), 100);
    const maxHourVal = Math.max(...hourlyData.map(h => h.sales), 100);

    // SVG Bar Chart for print
    const chartBarsHtml = data.map((d) => {
        const h1 = Math.max((d.value / maxBarVal) * 95, 4);
        const h2 = d.compareValue ? Math.max((d.compareValue / maxBarVal) * 95, 4) : 0;
        return `
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: flex-end; flex: 1; height: 130px; text-align: center;">
                <div style="font-size: 8.5px; font-weight: 700; color: #FF7A59; margin-bottom: 2px;">฿${d.value >= 1000 ? (d.value / 1000).toFixed(1) + 'k' : d.value}</div>
                <div style="display: flex; align-items: flex-end; gap: 3px; height: 95px;">
                    <div style="width: 14px; height: ${h1}px; background: #FF7A59; border-radius: 3px 3px 0 0;" title="ปัจจุบัน: ฿${d.value.toLocaleString()}"></div>
                    ${d.compareValue ? `<div style="width: 10px; height: ${h2}px; background: #CBD5E1; border-radius: 3px 3px 0 0;" title="ช่วงก่อน: ฿${d.compareValue.toLocaleString()}"></div>` : ''}
                </div>
                <div style="font-size: 9.5px; font-weight: 600; color: #334155; margin-top: 4px; border-top: 1px solid #E2E8F0; width: 100%; padding-top: 2px;">${d.label}</div>
            </div>
        `;
    }).join('');

    // Peak Hours Bars
    const hourlyBarsHtml = hourlyData.map(h => {
        const hVal = Math.max((h.sales / maxHourVal) * 40, 3);
        const isPeak = h.sales === maxHourVal;
        return `
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: flex-end; flex: 1; height: 60px; text-align: center;">
                <div style="width: 10px; height: ${hVal}px; background: ${isPeak ? '#10B981' : '#FFA07A'}; border-radius: 2px 2px 0 0;"></div>
                <div style="font-size: 7.5px; color: #64748B; margin-top: 2px;">${h.hour}</div>
            </div>
        `;
    }).join('');

    // Table Rows
    const tableRowsHtml = data.map((d, idx) => {
        const diff = d.compareValue && d.value > 0 ? (((d.value - d.compareValue) / d.compareValue) * 100).toFixed(1) : '-';
        const isPos = diff !== '-' && Number(diff) >= 0;
        return `
            <tr style="background: ${idx % 2 === 0 ? '#FFFFFF' : '#F8FAFC'};">
                <td style="font-weight: 700; text-align: center; padding: 6px 8px; border: 1px solid #E2E8F0;">${d.label}</td>
                <td style="font-weight: 700; color: #FF7A59; text-align: right; padding: 6px 8px; border: 1px solid #E2E8F0;">฿${d.value.toLocaleString()}</td>
                <td style="text-align: right; color: #64748B; padding: 6px 8px; border: 1px solid #E2E8F0;">${d.compareValue ? `฿${d.compareValue.toLocaleString()}` : '-'}</td>
                <td style="text-align: center; font-weight: 700; color: ${isPos ? '#10B981' : '#EF4444'}; padding: 6px 8px; border: 1px solid #E2E8F0;">
                    ${diff !== '-' ? (isPos ? `+${diff}%` : `${diff}%`) : '-'}
                </td>
                <td style="text-align: center; padding: 6px 8px; border: 1px solid #E2E8F0;">${d.bills || 0} บิล</td>
                <td style="text-align: right; padding: 6px 8px; border: 1px solid #E2E8F0;">฿${d.bills ? Math.round(d.value / d.bills).toLocaleString() : '-'}</td>
            </tr>
        `;
    }).join('');

    const viewModeLabel = viewMode === 'weekly' 
        ? 'สรุปเปรียบเทียบรายสัปดาห์ (Weekly Comparison)' 
        : viewMode === 'monthly' 
        ? 'สรุปแนวโน้มรายเดือน (Monthly Trend)' 
        : 'สรุปภาพรวมรายปี (Yearly Trend & Growth)';

    container.innerHTML = `
        ${getBaseHeader('รายงานวิเคราะห์สถิติยอดขาย (SALES ANALYTICS REPORT)', undefined, new Date().toLocaleString('th-TH'))}

        <div style="margin-bottom: 12px; font-size: 12px; color: #475569;">
            <strong>มุมมองการวิเคราะห์:</strong> ${viewModeLabel}
        </div>

        <!-- 4 KPI Boxes -->
        <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px;">
            <div style="background: #FFF3EE; border: 1px solid #FFD8CC; border-radius: 8px; padding: 10px; text-align: center;">
                <div style="font-size: 11px; color: #64748B; font-weight: 600;">ยอดขายรวม</div>
                <div style="font-size: 17px; font-weight: 800; color: #FF7A59; margin: 2px 0;">฿${totalSales.toLocaleString()}</div>
                <div style="font-size: 9.5px; color: #10B981; font-weight: 700;">▲ +${growthPercent}% เติบโต</div>
            </div>
            <div style="background: #EFF6FF; border: 1px solid #BFDBFE; border-radius: 8px; padding: 10px; text-align: center;">
                <div style="font-size: 11px; color: #64748B; font-weight: 600;">จำนวนบิลทั้งหมด</div>
                <div style="font-size: 17px; font-weight: 800; color: #2563EB; margin: 2px 0;">${totalBills.toLocaleString()} บิล</div>
                <div style="font-size: 9.5px; color: #64748B;">เฉลี่ย ฿${avgPerBill.toLocaleString()} / บิล</div>
            </div>
            <div style="background: #ECFDF5; border: 1px solid #A7F3D0; border-radius: 8px; padding: 10px; text-align: center;">
                <div style="font-size: 11px; color: #64748B; font-weight: 600;">ช่วงที่ขายดีที่สุด (Peak)</div>
                <div style="font-size: 14px; font-weight: 800; color: #059669; margin: 2px 0;">${peakText}</div>
                <div style="font-size: 9.5px; color: #64748B;">ยอดสูงสุดของรอบ</div>
            </div>
            <div style="background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 8px; padding: 10px; text-align: center;">
                <div style="font-size: 11px; color: #64748B; font-weight: 600;">ช่องทางชำระเงินหลัก</div>
                <div style="font-size: 14px; font-weight: 800; color: #1E293B; margin: 2px 0;">สแกน QR (58%)</div>
                <div style="font-size: 9.5px; color: #64748B;">เงินสด 31% | บัตร 11%</div>
            </div>
        </div>

        <!-- 2 Columns: Bar Chart & Payment/Category Breakdown -->
        <div style="display: grid; grid-template-columns: 3fr 2fr; gap: 12px; margin-bottom: 12px;">
            <!-- Bar Chart Section -->
            <div style="border: 1px solid #E2E8F0; border-radius: 8px; padding: 10px; background: #FFFFFF;">
                <div style="font-size: 11.5px; font-weight: 700; color: #1E293B; margin-bottom: 6px; display: flex; justify-content: space-between;">
                    <span>📈 แผนภูมิเปรียบเทียบยอดขาย (${viewMode === 'weekly' ? 'รายสัปดาห์' : viewMode === 'monthly' ? 'รายเดือน' : 'รายปี'})</span>
                    <span style="font-size: 9.5px; color: #64748B;">
                        <span style="display: inline-block; width: 7px; height: 7px; background: #FF7A59; border-radius: 2px;"></span> ช่วงนี้ 
                        <span style="display: inline-block; width: 7px; height: 7px; background: #CBD5E1; border-radius: 2px; margin-left: 6px;"></span> ช่วงก่อน
                    </span>
                </div>
                <div style="display: flex; gap: 4px; align-items: flex-end; padding-top: 6px;">
                    ${chartBarsHtml}
                </div>
            </div>

            <!-- Breakdown Shares -->
            <div style="border: 1px solid #E2E8F0; border-radius: 8px; padding: 10px; background: #FFFFFF; display: flex; flex-direction: column; justify-content: space-between;">
                <div>
                    <div style="font-size: 11.5px; font-weight: 700; color: #1E293B; margin-bottom: 4px;">💳 สัดส่วนการชำระเงิน</div>
                    ${paymentDonutData.map(p => `
                        <div style="margin-bottom: 4px;">
                            <div style="display: flex; justify-content: space-between; font-size: 10px; margin-bottom: 1px;">
                                <span>${p.label}</span>
                                <span style="font-weight: 700;">฿${p.value.toLocaleString()} (${p.percentage}%)</span>
                            </div>
                            <div style="height: 5px; background: #F1F5F9; border-radius: 3px; overflow: hidden;">
                                <div style="width: ${p.percentage}%; height: 100%; background: ${p.color};"></div>
                            </div>
                        </div>
                    `).join('')}
                </div>

                <div style="margin-top: 6px; border-top: 1px solid #F1F5F9; padding-top: 6px;">
                    <div style="font-size: 11.5px; font-weight: 700; color: #1E293B; margin-bottom: 4px;">🏷️ สัดส่วนตามหมวดหมู่สินค้า</div>
                    ${categoryDonutData.map(c => `
                        <div style="margin-bottom: 4px;">
                            <div style="display: flex; justify-content: space-between; font-size: 10px; margin-bottom: 1px;">
                                <span>${c.label}</span>
                                <span style="font-weight: 700;">฿${c.value.toLocaleString()} (${c.percentage}%)</span>
                            </div>
                            <div style="height: 5px; background: #F1F5F9; border-radius: 3px; overflow: hidden;">
                                <div style="width: ${c.percentage}%; height: 100%; background: ${c.color};"></div>
                            </div>
                        </div>
                    `).join('')}
                </div>
            </div>
        </div>

        <!-- Peak Hours Bar -->
        <div style="border: 1px solid #E2E8F0; border-radius: 8px; padding: 8px 10px; background: #FFFFFF; margin-bottom: 12px;">
            <div style="font-size: 10.5px; font-weight: 700; color: #1E293B; margin-bottom: 3px;">⏰ ช่วงเวลาขายดีในแต่ละวัน (08:00 - 20:00)</div>
            <div style="display: flex; gap: 2px; align-items: flex-end;">
                ${hourlyBarsHtml}
            </div>
        </div>

        <!-- Detailed Table -->
        <div style="margin-bottom: 14px;">
            <div style="font-size: 11.5px; font-weight: 700; color: #1E293B; margin-bottom: 4px;">📋 ตารางแจกแจงสถิติละเอียด</div>
            <table style="width: 100%; border-collapse: collapse; font-size: 10.5px;">
                <thead>
                    <tr style="background: #FFF3EE; color: #FF7A59; border: 1px solid #FFD8CC;">
                        <th style="padding: 5px 8px; border: 1px solid #FFD8CC; text-align: center;">${viewMode === 'weekly' ? 'วันในสัปดาห์' : viewMode === 'monthly' ? 'เดือน' : 'ปี'}</th>
                        <th style="padding: 5px 8px; border: 1px solid #FFD8CC; text-align: right;">ยอดขายปัจจุบัน</th>
                        <th style="padding: 5px 8px; border: 1px solid #FFD8CC; text-align: right;">ยอดขายช่วงก่อน</th>
                        <th style="padding: 5px 8px; border: 1px solid #FFD8CC; text-align: center;">การเติบโต (%)</th>
                        <th style="padding: 5px 8px; border: 1px solid #FFD8CC; text-align: center;">จำนวนบิล</th>
                        <th style="padding: 5px 8px; border: 1px solid #FFD8CC; text-align: right;">เฉลี่ย/บิล</th>
                    </tr>
                </thead>
                <tbody>
                    ${tableRowsHtml}
                </tbody>
            </table>
        </div>

        ${getSignatures('ผู้จัดทำรายงานสถิติ', 'ผู้จัดการร้าน', 'ผู้ตรวจสอบบัญชี')}
    `;

    printDocument(container, {
        documentType: 'a4-landscape',
        title: `รายงานวิเคราะห์สถิติยอดขาย_${viewMode}`
    });
};

// A net-profit figure is unknown until real sales are connected; unknown
// prints as a neutral grey "-", never as a coloured number.
const profitTone = (value: number | null) =>
    value === null
        ? { bg: '#F8FAFC', border: '#E2E8F0', fg: '#94A3B8' }
        : value >= 0
            ? { bg: '#F0F9FF', border: '#BAE6FD', fg: '#0284C7' }
            : { bg: '#FEF2F2', border: '#FECACA', fg: '#DC2626' };

export const printExpenseSummaryDocument = (params: {
    data: any[];
    kpi: {
        // null = no real sales figure is connected yet. The printed report
        // must say so rather than print a number, exactly as the screen does.
        totalIncome: number | null;
        totalAmount: number;
        netProfit: number | null;
        totalCount: number;
        topCatName: string;
        topCatAmount: number;
        receiptsCount: number;
    };
    periodText: string;
    categoryText: string;
    paymentText: string;
    userName?: string;
}) => {
    const { data, kpi, periodText, categoryText, paymentText, userName } = params;
    const container = document.createElement('div');
    container.style.padding = '20px';
    container.style.fontFamily = 'Kodchasan, Prompt, sans-serif';
    container.style.color = '#1E293B';
    container.style.background = '#FFFFFF';

    const tableRowsHtml = (data || []).map((item, idx) => `
        <tr style="border-bottom: 1px solid #E2E8F0; ${idx % 2 === 1 ? 'background: #F8FAFC;' : ''}">
            <td style="padding: 6px 8px; text-align: center;">${idx + 1}</td>
            <td style="padding: 6px 8px; white-space: nowrap;">${item.expense_date || '-'}</td>
            <td style="padding: 6px 8px; font-weight: 600; color: #FF7A59;">${item.doc_no || '-'}</td>
            <td style="padding: 6px 8px;">${item.category_name || '-'}</td>
            <td style="padding: 6px 8px; font-weight: 600;">${item.title || '-'}</td>
            <td style="padding: 6px 8px;">${item.payee || '-'}</td>
            <td style="padding: 6px 8px; text-align: center;">
                <span style="font-size: 10px; padding: 2px 6px; border-radius: 4px; background: #EEF2F6; color: #475569;">
                    ${item.payment_type_name || (item.payment_type === 1 ? 'เงินสด' : item.payment_type === 2 ? 'เงินโอน' : 'QR PromptPay')}
                </span>
            </td>
            <td style="padding: 6px 8px; text-align: right; font-weight: 700; color: #DC2626;">
                ฿${Number(item.total_amount || item.amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </td>
        </tr>
    `).join('');

    container.innerHTML = `
        ${getBaseHeader('รายงานสรุปค่าใช้จ่าย (EXPENSE REPORT)', undefined, new Date().toLocaleString('th-TH'))}

        <!-- Filter Context Banner -->
        <div style="display: flex; justify-content: space-between; align-items: center; background: #F8FAFC; border: 1px solid #E2E8F0; padding: 8px 14px; border-radius: 8px; margin-bottom: 14px; font-size: 11px; color: #475569;">
            <div>
                <strong>ช่วงเวลา:</strong> <span style="color: #FF7A59; font-weight: 700;">${periodText}</span> | 
                <strong>หมวดหมู่:</strong> <span>${categoryText}</span> | 
                <strong>ช่องทางชำระ:</strong> <span>${paymentText}</span>
            </div>
            <div>
                <strong>ผู้พิมพ์รายงาน:</strong> ${formatActor(userName, UNKNOWN_ACTOR_SHORT)} | <strong>รวมรายการ:</strong> ${data.length} รายการ
            </div>
        </div>

        <!-- 4 KPI Summary Cards -->
        <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px; margin-bottom: 16px;">
            <div style="background: #ECFDF5; border: 1px solid #A7F3D0; border-radius: 8px; padding: 10px; text-align: center;">
                <div style="font-size: 10.5px; color: #059669; font-weight: 700;">🛒 รายรับ (ยอดขายหน้าร้าน)</div>
                <div style="font-size: ${kpi.totalIncome === null ? '12px' : '16px'}; font-weight: 800; color: #059669; margin: 3px 0;">
                    ${kpi.totalIncome === null ? 'ไม่ทราบ' : `฿${kpi.totalIncome.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`}
                </div>
                <div style="font-size: 9.5px; color: #059669;">${kpi.totalIncome === null ? 'ไม่ได้รับข้อมูลยอดขายขณะพิมพ์' : `จากบิลขาย ${kpi.receiptsCount} ใบ`}</div>
            </div>

            <div style="background: #FFF7ED; border: 1px solid #FFEDD5; border-radius: 8px; padding: 10px; text-align: center;">
                <div style="font-size: 10.5px; color: #C2410C; font-weight: 700;">💸 รายจ่ายรวมทั้งหมด</div>
                <div style="font-size: 16px; font-weight: 800; color: #C2410C; margin: 3px 0;">
                    ฿${kpi.totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                </div>
                <div style="font-size: 9.5px; color: #C2410C;">บันทึกแล้ว ${kpi.totalCount} รายการ</div>
            </div>

            <div style="background: ${profitTone(kpi.netProfit).bg}; border: 1px solid ${profitTone(kpi.netProfit).border}; border-radius: 8px; padding: 10px; text-align: center;">
                <div style="font-size: 10.5px; color: ${profitTone(kpi.netProfit).fg}; font-weight: 700;">📈 กำไรสุทธิ (Net Profit)</div>
                <div style="font-size: 16px; font-weight: 800; color: ${profitTone(kpi.netProfit).fg}; margin: 3px 0;">
                    ${kpi.netProfit === null ? '-' : `฿${kpi.netProfit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`}
                </div>
                <div style="font-size: 9.5px; color: ${profitTone(kpi.netProfit).fg};">${kpi.netProfit === null ? 'คำนวณได้เมื่อมีรายรับจริง' : 'รายรับ - รายจ่ายรวม'}</div>
            </div>

            <div style="background: #FEF3C7; border: 1px solid #FDE68A; border-radius: 8px; padding: 10px; text-align: center;">
                <div style="font-size: 10.5px; color: #D97706; font-weight: 700;">🏷️ หมวดหมู่ที่จ่ายมากสุด</div>
                <div style="font-size: 13px; font-weight: 800; color: #D97706; margin: 4px 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                    ${kpi.topCatName || '-'}
                </div>
                <div style="font-size: 9.5px; color: #D97706;">฿${kpi.topCatAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
            </div>
        </div>

        <!-- Detailed Table -->
        <div style="margin-bottom: 16px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 10.5px;">
                <thead>
                    <tr style="background: #FFF3EE; color: #FF7A59; border: 1px solid #FFD8CC;">
                        <th style="padding: 6px 8px; border: 1px solid #FFD8CC; text-align: center; width: 35px;">#</th>
                        <th style="padding: 6px 8px; border: 1px solid #FFD8CC; text-align: left; width: 85px;">วันที่</th>
                        <th style="padding: 6px 8px; border: 1px solid #FFD8CC; text-align: left; width: 95px;">เลขที่เอกสาร</th>
                        <th style="padding: 6px 8px; border: 1px solid #FFD8CC; text-align: left; width: 110px;">หมวดหมู่</th>
                        <th style="padding: 6px 8px; border: 1px solid #FFD8CC; text-align: left;">ชื่อรายการค่าใช้จ่าย</th>
                        <th style="padding: 6px 8px; border: 1px solid #FFD8CC; text-align: left; width: 100px;">ผู้รับเงิน</th>
                        <th style="padding: 6px 8px; border: 1px solid #FFD8CC; text-align: center; width: 85px;">ช่องทางชำระ</th>
                        <th style="padding: 6px 8px; border: 1px solid #FFD8CC; text-align: right; width: 100px;">ยอดเงิน (บาท)</th>
                    </tr>
                </thead>
                <tbody>
                    ${tableRowsHtml.length > 0 ? tableRowsHtml : `
                        <tr>
                            <td colspan="8" style="text-align: center; padding: 24px; color: #94A3B8;">ไม่มีรายการค่าใช้จ่ายตามเงื่อนไขที่เลือก</td>
                        </tr>
                    `}
                </tbody>
                <tfoot>
                    <tr style="background: #F8FAFC; border: 1.5px solid #CBD5E1; font-weight: 800; font-size: 11px;">
                        <td colspan="7" style="padding: 8px 10px; text-align: right;">ยอดรวมค่าใช้จ่ายทั้งสิ้น:</td>
                        <td style="padding: 8px 10px; text-align: right; color: #DC2626; font-size: 12px;">
                            ฿${kpi.totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>

        ${getSignatures('ผู้บันทึก / เจ้าหน้าที่บัญชี', 'ผู้จัดการร้าน', 'ผู้ตรวจสอบ / เจ้าของกิจการ')}
    `;

    printDocument(container, {
        documentType: 'a4-portrait',
        title: `รายงานสรุปค่าใช้จ่าย_${new Date().toISOString().slice(0, 10)}`
    });
};

